/**
 * The OBS fixture (SPEC §4): the one supported scene collection, declared per
 * project in `fixture.json`. Preflight validates OBS against it and the show
 * assumes it. The apps never create or reconfigure inputs.
 *
 * The schema has no defaults. The default names live in the committed
 * `fixtures/default.fixture.json`, which a project copies and edits.
 */
import { z } from 'zod';
import { REFERENCE_CANVAS } from './layout.ts';
import { findDuplicates, parseOrThrow } from './validate.ts';

export const FIXTURE_SCHEMA_VERSION = 1;

/** Nested camera input scenes. `CAM-1` is the host; `CAM-2`…`CAM-6` follow sheet `speaker_order`. */
export const CAMERA_IDS = ['CAM-1', 'CAM-2', 'CAM-3', 'CAM-4', 'CAM-5', 'CAM-6'] as const;
export const CameraIdSchema = z.enum(CAMERA_IDS);
export type CameraId = z.infer<typeof CameraIdSchema>;

/** The nested screenshare scene: a separate CoreVideo output (SPEC §17). */
export const SHARE_ID = 'SHARE';

/** Single scenes, each showing one camera. */
export const SINGLE_SCENE_KEYS = [
  'hostSingle',
  'speaker1Single',
  'speaker2Single',
  'speaker3Single',
  'speaker4Single',
  'speaker5Single',
] as const;
export type SingleSceneKey = (typeof SINGLE_SCENE_KEYS)[number];

/** Which camera each Single scene shows (`Host Single` ← CAM-1, `Speaker n Single` ← CAM-(n+1)). */
export const SINGLE_SCENE_CAMERA: Readonly<Record<SingleSceneKey, CameraId>> = {
  hostSingle: 'CAM-1',
  speaker1Single: 'CAM-2',
  speaker2Single: 'CAM-3',
  speaker3Single: 'CAM-4',
  speaker4Single: 'CAM-5',
  speaker5Single: 'CAM-6',
};

/** Camera scenes: layered GFX Background → CAM-n items → GFX Cameras → GFX Overlay. */
export const CAMERA_SCENE_KEYS = ['main', ...SINGLE_SCENE_KEYS, 'speakerShare'] as const;
export const CameraSceneKeySchema = z.enum(CAMERA_SCENE_KEYS);
export type CameraSceneKey = z.infer<typeof CameraSceneKeySchema>;

/** Full-screen scenes: one `GFX Scene <name>` browser source draws everything. */
export const FULLSCREEN_SCENE_KEYS = ['starting', 'brb', 'nextSession', 'end'] as const;
export const FullscreenSceneKeySchema = z.enum(FULLSCREEN_SCENE_KEYS);
export type FullscreenSceneKey = z.infer<typeof FullscreenSceneKeySchema>;

export const SceneKeySchema = z.enum([...CAMERA_SCENE_KEYS, ...FULLSCREEN_SCENE_KEYS]);
export type SceneKey = z.infer<typeof SceneKeySchema>;

const obsName = z.string().trim().min(1).max(256);

/** A nested input scene holding exactly one CoreVideo input. */
const nestedInputScene = <Id extends string>(id: Id) =>
  z.strictObject({
    id: z.literal(id),
    /** OBS scene name of the nested scene. */
    sceneName: obsName,
    /**
     * OBS input name of the CoreVideo source inside the nested scene. Also the
     * `source` value CoreVideo's `assign_output` addresses.
     */
    coreVideoInput: obsName,
  });

const cameraSceneEntry = z.strictObject({
  sceneName: obsName,
  /** This scene's own `GFX Cameras` browser source (one per camera scene). */
  gfxCamerasInput: obsName,
});

const fullscreenSceneEntry = z.strictObject({
  sceneName: obsName,
  /** The `GFX Scene <name>` browser source that draws the whole scene. */
  gfxSceneInput: obsName,
});

export const FixtureSchema = z
  .strictObject({
    schemaVersion: z.literal(FIXTURE_SCHEMA_VERSION),
    canvas: z.strictObject({
      width: z.literal(REFERENCE_CANVAS.width),
      height: z.literal(REFERENCE_CANVAS.height),
    }),
    /** Exactly six, in order CAM-1 … CAM-6. */
    cameras: z.tuple([
      nestedInputScene('CAM-1'),
      nestedInputScene('CAM-2'),
      nestedInputScene('CAM-3'),
      nestedInputScene('CAM-4'),
      nestedInputScene('CAM-5'),
      nestedInputScene('CAM-6'),
    ]),
    share: nestedInputScene(SHARE_ID),
    cameraScenes: z.strictObject({
      main: cameraSceneEntry,
      hostSingle: cameraSceneEntry,
      speaker1Single: cameraSceneEntry,
      speaker2Single: cameraSceneEntry,
      speaker3Single: cameraSceneEntry,
      speaker4Single: cameraSceneEntry,
      speaker5Single: cameraSceneEntry,
      speakerShare: cameraSceneEntry,
    }),
    fullscreenScenes: z.strictObject({
      starting: fullscreenSceneEntry,
      brb: fullscreenSceneEntry,
      nextSession: fullscreenSceneEntry,
      end: fullscreenSceneEntry,
    }),
    gfx: z.strictObject({
      /** URL1, one input reused at the bottom of every camera scene. */
      backgroundInput: obsName,
      /** URL3, one input reused at the top of every camera scene. */
      overlayInput: obsName,
    }),
    /** Inputs the control app mixes (mute and level only). */
    audioInputs: z
      .array(z.strictObject({ inputName: obsName, label: z.string().trim().min(1).max(64) }))
      .min(1)
      .max(16),
  })
  .superRefine((f, ctx) => {
    const sceneNames = [
      ...f.cameras.map((c) => c.sceneName),
      f.share.sceneName,
      ...Object.values(f.cameraScenes).map((s) => s.sceneName),
      ...Object.values(f.fullscreenScenes).map((s) => s.sceneName),
    ];
    for (const dup of findDuplicates(sceneNames)) {
      ctx.addIssue({ code: 'custom', path: [], message: `scene name "${dup}" is used more than once` });
    }
    const inputNames = [
      ...f.cameras.map((c) => c.coreVideoInput),
      f.share.coreVideoInput,
      ...Object.values(f.cameraScenes).map((s) => s.gfxCamerasInput),
      ...Object.values(f.fullscreenScenes).map((s) => s.gfxSceneInput),
      f.gfx.backgroundInput,
      f.gfx.overlayInput,
    ];
    for (const dup of findDuplicates(inputNames)) {
      ctx.addIssue({ code: 'custom', path: [], message: `input name "${dup}" is used more than once` });
    }
    // OBS shares one namespace for scenes and inputs (both are sources).
    for (const clash of sceneNames.filter((n) => inputNames.includes(n))) {
      ctx.addIssue({ code: 'custom', path: [], message: `"${clash}" is both a scene and an input name` });
    }
    for (const dup of findDuplicates(f.audioInputs.map((a) => a.inputName))) {
      ctx.addIssue({ code: 'custom', path: ['audioInputs'], message: `audio input "${dup}" is listed twice` });
    }
  });
export type Fixture = z.infer<typeof FixtureSchema>;

/** Validates a parsed `fixture.json`. @throws ValidationError listing every problem. */
export function parseFixture(data: unknown, fileName = 'fixture.json'): Fixture {
  return parseOrThrow(FixtureSchema, data, `fixture file "${fileName}"`);
}

/** Every GFX browser-source input the fixture declares (preflight checks their settings). */
export function gfxInputNames(f: Fixture): string[] {
  return [
    f.gfx.backgroundInput,
    f.gfx.overlayInput,
    ...Object.values(f.cameraScenes).map((s) => s.gfxCamerasInput),
    ...Object.values(f.fullscreenScenes).map((s) => s.gfxSceneInput),
  ];
}
